import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getSessionRecents, subscribeSessionRecents } from "@/lib/sessionRecents";
import { normalizeQuery } from "@/lib/normalizeQuery";

export interface SeedItem {
  text: string;
  source: "recent" | "top";
}

/**
 * Pre-fetches the instant list shown in the mobile fullscreen search overlay
 * BEFORE the user focuses the bar, so the panel appears with content the
 * moment focus opens.
 *
 * Sizing rules (matches product spec):
 *   - Signed in:    up to 7 recents (session + user DB) + up to 10 trending
 *                   (last 7 days, site-wide). Recents always rendered first.
 *   - Signed out:   no recents, up to 10 trending only.
 *
 * Recents = session recents (highest priority, instant) merged with the
 * signed-in user's own DB history. Trending = top site-wide queries from the
 * last 7 days. Lists are de-duplicated case/whitespace-insensitively.
 */
export function useInstantSearchSeed(_legacyLimit?: number): SeedItem[] {
  const { user } = useAuth();
  const [serverItems, setServerItems] = useState<SeedItem[]>([]);
  const [sessionRecents, setSessionRecents] = useState<string[]>(() => getSessionRecents());

  // Subscribe to local push events from search submissions so the seed
  // updates the moment a query is submitted, with no DB roundtrip.
  useEffect(() => {
    setSessionRecents(getSessionRecents());
    return subscribeSessionRecents(setSessionRecents);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const recent: string[] = [];
      if (user?.id) {
        const { data } = await supabase
          .from("search_logs")
          .select("query, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(30);
        const seen = new Set<string>();
        for (const row of data ?? []) {
          const q = (row.query || "").trim();
          if (!q || q.length < 3 || q.length > 140) continue;
          const key = normalizeQuery(q);
          if (!key || seen.has(key)) continue;
          seen.add(key);
          recent.push(q);
          if (recent.length >= 7) break;
        }
      }

      // Top site-wide: last 7 days, exclude bots, group by normalized query.
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: topData } = await supabase
        .from("search_logs")
        .select("query, normalized_query")
        .gte("created_at", since)
        .or("is_bot.is.null,is_bot.eq.false")
        .gt("results_count", 0)
        .limit(2000);

      const counts = new Map<string, { count: number; display: string }>();
      for (const row of topData ?? []) {
        const q = (row.query || "").trim();
        if (!q || q.length < 6 || q.length > 140) continue;
        const key = normalizeQuery(row.normalized_query || q);
        if (!key) continue;
        const cur = counts.get(key);
        if (cur) cur.count += 1;
        else counts.set(key, { count: 1, display: q });
      }

      const recentKeys = new Set(recent.map(normalizeQuery));
      const top = Array.from(counts.values())
        .sort((a, b) => b.count - a.count)
        .map((v) => v.display)
        .filter((q) => !recentKeys.has(normalizeQuery(q)))
        .slice(0, 10);

      if (cancelled) return;

      const merged: SeedItem[] = [
        ...recent.map((text) => ({ text, source: "recent" as const })),
        ...top.map((text) => ({ text, source: "top" as const })),
      ];

      setServerItems(merged);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Merge session recents (highest priority, only when signed in) in front of
  // server data. Anonymous users see ONLY trending — recents require auth so
  // we don't leak someone else's queries on a shared device.
  const seen = new Set<string>();
  const merged: SeedItem[] = [];

  if (user?.id) {
    for (const text of sessionRecents) {
      const key = normalizeQuery(text);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push({ text, source: "recent" });
      if (merged.filter((m) => m.source === "recent").length >= 7) break;
    }
  }

  let recentCount = merged.length;
  for (const item of serverItems) {
    if (item.source === "recent" && !user?.id) continue;
    if (item.source === "recent" && recentCount >= 7) continue;
    const key = normalizeQuery(item.text);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(item);
    if (item.source === "recent") recentCount += 1;
  }

  return merged;
}
